import type { Source } from '../types';
import {
  SourceType,
  SourceSyncStatus,
  SuggestedSourceType,
  SOURCE_STALE_THRESHOLD_MS,
} from './Constants';

/**
 * Mappe le type d'une source suggérée par l'IA (`/sources/suggest` : RSS /
 * NEWSLETTER / WEBSITE) vers le `SourceType` de persistence. Le backend
 * (`SourceType` dans `app/models/models_enum.py`) n'a pas de valeur
 * `newsletter` : une newsletter est stockée comme un site web. Toute valeur
 * inconnue retombe aussi sur WEBSITE (le backend refuserait sinon la création).
 */
export function mapSuggestedTypeToSourceType(type: SuggestedSourceType | string): SourceType {
  switch (type) {
    case SuggestedSourceType.RSS:
      return SourceType.RSS;
    case SuggestedSourceType.NEWSLETTER:
    case SuggestedSourceType.WEBSITE:
    default:
      return SourceType.WEBSITE;
  }
}

// Une sync en cours ou planifiée n'est pas « en retard ».
const IN_PROGRESS_SYNC_STATUSES = new Set<string>([
  SourceSyncStatus.PENDING,
  SourceSyncStatus.SYNCING,
]);

function toTime(value: Date | string | undefined): number | null {
  if (!value) return null;
  const time = typeof value === 'string' ? new Date(value).getTime() : value.getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * Une source est périmée (badge « non synchronisée depuis 7 jours ») si :
 *   - sa dernière sync date de plus de 7 jours ;
 *   - OU elle n'a jamais été synchronisée, existe depuis plus de 7 jours et
 *     n'a pas de sync en cours (`pending`/`syncing`).
 * Une source toute neuve (jamais synchronisée, créée il y a moins de 7 jours, ou
 * date de création inconnue) n'est PAS périmée. Les sources `raw_text` (texte
 * brut interne) ne se synchronisent jamais : elles sont exclues.
 */
export function isSourceStale(
  source: Pick<Source, 'type' | 'lastSync' | 'createdAt' | 'syncStatus'>,
  now: number = Date.now(),
): boolean {
  if (source.type === SourceType.RAW_TEXT) return false;

  const lastSyncTime = toTime(source.lastSync);
  if (lastSyncTime !== null) {
    return now - lastSyncTime > SOURCE_STALE_THRESHOLD_MS;
  }

  if (source.syncStatus && IN_PROGRESS_SYNC_STATUSES.has(source.syncStatus)) return false;

  const createdTime = toTime(source.createdAt);
  return createdTime !== null && now - createdTime > SOURCE_STALE_THRESHOLD_MS;
}
